import FramedBlock from "@/components/FramedBlock";
import { isFramed } from "@/lib/frameGuard";
import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { WifiOff } from "lucide-react";
import BevidLogo from "@/components/BevidLogo";
import PasswordInput from "@/components/PasswordInput";
import LegalFooter from "@/components/LegalFooter";
import { t } from "@/lib/i18n";
import { usePageMeta } from "@/lib/pageMeta";

export default function Login() {
  usePageMeta({
    title: "Connexion · BEVID — Le CRM des brasseurs",
    canonical: "/login",
    description: "Le CRM des brasseurs : votre DRM, vos fûts, vos tournées — même sans réseau.",
  });
  const { login } = useAuth();
  const { t, lang, setLanguage, languages } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [netError, setNetError] = useState(null);
  const [rateError, setRateError] = useState(null);
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname && location.state.from.pathname !== "/login"
    ? location.state.from.pathname + (location.state.from.search || "")
    : null;

  const submit = async (e) => {
    e.preventDefault();
    setErrors({}); setNetError(null); setRateError(null);
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.language) setLanguage(u.language);
      toast.success(t("login") + " ✓");
      // Redirection : deep-link explicite > home_screen préférence > racine
      const dest = u.must_reset ? "/change-password" : (from || u.home_screen || "/");
      nav(dest, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (!err.response) {
        setNetError("Connexion impossible. Vérifiez votre réseau et réessayez.");
      } else if (status === 422 && data?.errors) {
        setErrors(data.errors);
      } else if (status === 429) {
        // Message dans le formulaire, pas seulement en toast
        setRateError(data?.detail || t("toast.login_ratelimit"));
      } else {
        setErrors({ password: data?.detail || "Identifiants invalides" });
      }
    } finally { setLoading(false); }
  };

  if (isFramed()) return <FramedBlock />;
  return (
    <div className="min-h-screen flex">
      <div data-testid="brand-panel" className="hidden md:flex md:w-1/2 relative bg-[var(--ds-accent)] text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 opacity-90" aria-hidden="true" style={{ background: "radial-gradient(circle at 20% 20%, #6B2470 0%, transparent 55%), radial-gradient(circle at 80% 70%, #B076B4 0%, transparent 45%), linear-gradient(135deg, #4A154B 0%, #2B0C2D 100%)" }} />
        <div className="absolute inset-0 opacity-10" aria-hidden="true" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative z-10 flex items-center gap-2">
          <BevidLogo size={28} />
          <span className="overline text-white/80">BEVID · CRM</span>
        </div>
        <div className="relative z-10">
          <h2 className="font-display text-5xl leading-tight mb-4">Le CRM des brasseurs.<br/>Votre DRM, vos fûts, vos tournées.</h2>
          <p className="text-white/80 max-w-md">Même sans réseau. Terrain, DRM automatique depuis vos stocks, tournées optimisées, prise de commande hors-ligne.</p>
        </div>
        <div className="relative z-10 overline text-white/50">© 2026 BEVID</div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form" noValidate>
          <div className="flex items-start justify-between">
            <div>
              <p className="overline">{t("welcome")}</p>
              <h1 className="font-display text-4xl mt-1">{t("login")}</h1>
            </div>
            <select
              id="login-lang" name="language"
              value={lang}
              onChange={(e) => setLanguage(e.target.value)}
              className="text-sm border border-stone-300 rounded-lg px-2 py-1.5 bg-white min-h-[44px]"
              data-testid="login-lang-select"
              aria-label={t("language")}
            >
              {languages.map((l) => (
                <option key={l.code} value={l.code}>{l.flag} {l.label}</option>
              ))}
            </select>
          </div>

          {netError && (
            <div role="alert" data-testid="login-net-error"
                 style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", background: "var(--ds-state-danger-soft-bg)", color: "var(--ds-state-danger-soft-fg)", borderRadius: "var(--ds-radius-md)", border: "1px solid var(--ds-state-danger)" }}>
              <WifiOff size={16} aria-hidden="true" />
              <span className="text-sm">{netError}</span>
            </div>
          )}

          <div>
            <label htmlFor="login-email" className="text-xs text-stone-600 dark:text-stone-300 block">{t("email")}</label>
            <input id="login-email" name="email" type="email" autoComplete="username" required inputMode="email"
              value={email} onChange={e => setEmail(e.target.value)}
              aria-invalid={!!errors.email} aria-describedby={errors.email ? "login-email-err" : undefined}
              className="input-crm mt-1 min-h-[48px]" data-testid="login-email" />
            {errors.email && <p id="login-email-err" className="text-xs mt-1" style={{ color: "var(--ds-state-danger-text)" }}>{errors.email}</p>}
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="login-password" className="text-xs text-stone-600 dark:text-stone-300">{t("password")}</label>
              <Link to="/forgot-password" className="text-xs text-[var(--ds-accent)] font-medium hover:underline inline-flex items-center min-h-[44px] px-1" data-testid="login-forgot-link">
                {t("forgot_password")}
              </Link>
            </div>
            <PasswordInput id="login-password" name="password" required value={password}
              aria-invalid={!!errors.password} aria-describedby={errors.password ? "login-password-err" : undefined}
              onChange={e => setPassword(e.target.value)} className="mt-1" autoComplete="current-password" data-testid="login-password" />
            {errors.password && <p id="login-password-err" className="text-xs mt-1" style={{ color: "var(--ds-state-danger-text)" }}>{errors.password}</p>}
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full min-h-[48px]" data-testid="login-submit">
            {loading ? "…" : t("login")}
          </button>
          {rateError && (
            <p role="alert" data-testid="login-rate-error"
               className="text-sm text-center"
               style={{
                 color: "var(--ds-state-danger-soft-fg)",
                 background: "var(--ds-state-danger-soft-bg)",
                 border: "1px solid var(--ds-state-danger)",
                 padding: "10px 12px", borderRadius: "var(--ds-radius-md)",
               }}>{rateError}</p>
          )}
          <p className="text-sm text-stone-500 text-center flex items-center justify-center gap-3 flex-wrap">
            {!window.Capacitor?.isNativePlatform?.() && <Link to="/register" className="text-[var(--ds-accent)] font-medium inline-flex items-center min-h-[44px] px-2">{t("register")}</Link>}
            <span aria-hidden="true">·</span>
            <Link to="/pricing" className="text-[var(--ds-accent)] font-medium inline-flex items-center min-h-[44px] px-2" data-testid="login-pricing-link">{t("see_pricing")}</Link>
          </p>
          <div className="pt-2 border-t border-stone-100 dark:border-stone-800 flex flex-wrap justify-center gap-x-1 gap-y-1 text-xs text-stone-400" aria-label="Liens légaux">
            <Link to="/legal/mentions-legales" className="hover:text-[var(--ds-accent)] hover:underline inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-3" data-testid="login-mentions">Mentions légales</Link>
            <Link to="/legal/cgv" className="hover:text-[var(--ds-accent)] hover:underline inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-3" data-testid="login-cgv">CGV</Link>
            <Link to="/legal/cgu" className="hover:text-[var(--ds-accent)] hover:underline inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-3" data-testid="login-cgu">CGU</Link>
            <Link to="/legal/politique-confidentialite" className="hover:text-[var(--ds-accent)] hover:underline inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-3" data-testid="login-privacy">Confidentialité</Link>
            <Link to="/legal/cookies" className="hover:text-[var(--ds-accent)] hover:underline inline-flex items-center justify-center min-h-[44px] min-w-[44px] px-3" data-testid="login-cookies">Cookies</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
